import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, TrendingUp, Clock, Users, Hash, Server, Calendar, Video, MessageSquare, ShieldCheck, Command, Package, Trophy, Hammer, Globe } from "lucide-react";
import { globalSearch, fetchSearchTrending, logSearchQuery } from "@/lib/api";
import { authClient } from "@/lib/auth";
import { useAuth } from "@/lib/auth";
import { sfx } from "@/lib/sounds";

const TYPE_META = {
  all:          { label: "All",          icon: Search,        color: "text-[#FFBF00]" },
  creators:     { label: "Creators",     icon: Video,         color: "text-pink-300" },
  communities:  { label: "Communities",  icon: Hash,          color: "text-purple-300" },
  servers:      { label: "Servers",      icon: Server,        color: "text-green-300" },
  events:       { label: "Events",       icon: Calendar,      color: "text-orange-300" },
  videos:       { label: "Videos",       icon: Video,         color: "text-red-300" },
  products:     { label: "Products",     icon: Package,       color: "text-emerald-300" },
  builds:       { label: "Builds",       icon: Hammer,        color: "text-amber-300" },
  leaderboards: { label: "Leaderboards", icon: Trophy,        color: "text-yellow-300" },
  pages:        { label: "Pages",        icon: Globe,         color: "text-sky-300" },
};

const RESULT_TYPE_META = {
  creator:     { icon: Video,         color: "text-pink-300",   badge: "CREATOR" },
  community:   { icon: Hash,          color: "text-purple-300", badge: "COMMUNITY" },
  server:      { icon: Server,        color: "text-green-300",  badge: "SERVER" },
  event:       { icon: Calendar,      color: "text-orange-300", badge: "EVENT" },
  video:       { icon: Video,         color: "text-red-300",    badge: "VIDEO" },
  product:     { icon: Package, color: "text-emerald-300", badge: "PRODUCT" },
  build:       { icon: Hammer,  color: "text-amber-300",   badge: "BUILD" },
  leaderboard: { icon: Trophy,  color: "text-yellow-300",  badge: "LEADERBOARD" },
  page:        { icon: Globe,   color: "text-sky-300",     badge: "PAGE" },
};

function HighlightText({ text, ranges }) {
  if (!text || !ranges || ranges.length === 0) return <>{text}</>;
  const parts = [];
  let cursor = 0;
  for (const [s, e] of ranges) {
    if (s > cursor) parts.push(text.slice(cursor, s));
    parts.push(
      <mark key={s} className="search-highlight bg-[#FFBF00]/20 text-[#FFBF00] px-0.5 rounded">
        {text.slice(s, e)}
      </mark>
    );
    cursor = e;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <>{parts}</>;
}

function ResultRow({ item, active, onSelect, testIdx }) {
  const M = RESULT_TYPE_META[item.type] || RESULT_TYPE_META.creator;
  const Icon = M.icon;
  const hl = item.highlight || {};
  return (
    <button
      type="button"
      data-testid={`search-result-${testIdx}`}
      data-active={active ? "1" : "0"}
      onClick={() => onSelect(item)}
      onMouseEnter={() => sfx.hover()}
      className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors border ${
        active
          ? "bg-[#FFBF00]/10 border-[#FFBF00]/40"
          : "border-transparent hover:bg-white/5"
      }`}
    >
      {item.avatar ? (
        <img
          src={item.avatar}
          alt=""
          className="w-9 h-9 rounded-md object-cover shrink-0 bg-white/5"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <div className={`w-9 h-9 rounded-md flex items-center justify-center bg-white/5 shrink-0 ${M.color}`}>
          <Icon size={16} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-white">
            <HighlightText text={item.title} ranges={hl.title} />
          </span>
          <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded ${M.color} bg-white/5 shrink-0`}>
            {M.badge}
          </span>
        </div>
        {item.subtitle && (
          <div className="text-xs text-white/50 truncate mt-0.5">
            <HighlightText text={item.subtitle} ranges={hl.subtitle} />
          </div>
        )}
      </div>
    </button>
  );
}

export default function GlobalSearch() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [data, setData] = useState(null);
  const [trending, setTrending] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const isAdmin = user?.role === "admin";
  const typeTabs = useMemo(() => {
    const keys = ["all", "creators", "communities", "servers", "events", "videos", "products", "builds", "leaderboards", "pages"];
    return keys;
  }, [isAdmin]);

  // ---------- Open/close hotkeys ----------
  useEffect(() => {
    const onKey = (e) => {
      const isCmdK = (e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K");
      if (isCmdK) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 20);
      // load trending + recent
      fetchSearchTrending(8).then(setTrending).catch(() => setTrending([]));
      if (user) {
        authClient.get("/search/recent?limit=6").then((r) => setRecent(r.data || [])).catch(() => setRecent([]));
      } else {
        setRecent([]);
      }
    } else {
      // reset for next open
      setQ("");
      setData(null);
    }
  }, [open, user]);

  // ---------- Debounced query ----------
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim() && type === "all") {
      setData(null);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await globalSearch(q, type, 8);
        setData(res);
        setActive(0);
      } catch {
        setData({ total: 0, top: [], results: {} });
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, type, open]);

  const flatResults = useMemo(() => {
    if (!data) return [];
    if (type === "all") return data.top || [];
    return (data.results && data.results[type]) || [];
  }, [data, type]);

  const onSelect = useCallback((item) => {
    if (!item) return;
    if (q.trim()) logSearchQuery(q.trim());
    setOpen(false);
    sfx.click();
    if (item.url?.startsWith("http")) {
      window.open(item.url, "_blank", "noopener");
    } else if (item.url) {
      nav(item.url);
    }
  }, [q, nav]);

  const onSubmit = useCallback(() => {
    if (!q.trim()) return;
    logSearchQuery(q.trim());
    setOpen(false);
    sfx.click();
    nav(`/search?q=${encodeURIComponent(q.trim())}${type !== "all" ? `&type=${type}` : ""}`);
  }, [q, type, nav]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, Math.max(flatResults.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (flatResults[active]) onSelect(flatResults[active]);
      else onSubmit();
    }
  };

  const clearRecent = async () => {
    try {
      await authClient.delete("/search/recent");
      setRecent([]);
    } catch { /* ignore */ }
  };

  const isMac = typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent || navigator.platform);

  return (
    <>
      {/* Trigger button (Navbar - PC & Tablet) */}
      <button
        type="button"
        data-testid="global-search-trigger"
        onClick={() => setOpen(true)}
        onMouseEnter={() => sfx.hover()}
        className="hidden md:flex items-center justify-between h-9 px-3 min-w-[180px] max-w-[240px] rounded-full bg-white/5 border border-white/10 hover:border-[#FFBF00]/40 text-sm text-white/60 transition-all group"
        aria-label="Search"
      >
        <div className="flex items-center gap-2 truncate pr-2">
          <Search size={14} className="shrink-0 text-white/50 group-hover:text-[#FFBF00] transition-colors" />
          <span className="truncate text-xs">Search…</span>
        </div>
        <kbd className="shrink-0 inline-flex items-center gap-0.5 text-[10px] text-white/40 bg-white/10 border border-white/10 rounded px-1.5 py-0.5 font-mono leading-none">
          {isMac ? <><Command size={9} />K</> : "Ctrl K"}
        </kbd>
      </button>

      {/* Mobile icon-only trigger */}
      <button
        type="button"
        data-testid="global-search-trigger-mobile"
        onClick={() => setOpen(true)}
        className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/5 border border-white/10 text-white/70 active:scale-95 transition-transform"
        aria-label="Search"
      >
        <Search size={16} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[100] flex items-start justify-center pt-4 md:pt-[8vh] px-3 sm:px-4"
            onClick={() => setOpen(false)}
            data-testid="global-search-overlay"
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ y: -12, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: -8, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.18, ease: [0.2, 0.9, 0.3, 1] }}
              className="relative w-full max-w-2xl max-h-[85vh] sm:max-h-[80vh] flex flex-col rounded-2xl border border-white/10 bg-[#0a0a0a]/95 shadow-[0_20px_80px_rgba(0,0,0,0.8),0_0_0_1px_rgba(255,191,0,0.1)] overflow-hidden"
              data-testid="global-search-panel"
            >
              {/* Search input header */}
              <div className="flex items-center gap-2.5 px-3.5 py-3 border-b border-white/10 shrink-0">
                <Search size={18} className="text-[#FFBF00] shrink-0" />
                <input
                  ref={inputRef}
                  data-testid="global-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Search creators, servers, events, products, builds…"
                  className="flex-1 min-w-0 bg-transparent outline-none text-white placeholder:text-white/30 text-sm sm:text-base"
                />
                {loading && <div className="w-4 h-4 border-2 border-[#FFBF00]/40 border-t-[#FFBF00] rounded-full animate-spin shrink-0" />}
                <button
                  data-testid="global-search-close"
                  onClick={() => setOpen(false)}
                  className="text-white/40 hover:text-white p-1.5 rounded-lg hover:bg-white/5 shrink-0 transition-colors"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Filter chips horizontal bar */}
              <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/10 overflow-x-auto no-scrollbar shrink-0">
                {typeTabs.map((t) => {
                  const M = TYPE_META[t];
                  const Icon = M.icon;
                  const activeChip = t === type;
                  return (
                    <button
                      key={t}
                      data-testid={`global-search-type-${t}`}
                      onClick={() => { setType(t); setActive(0); }}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 sm:py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors touch-manipulation ${
                        activeChip
                          ? "bg-[#FFBF00] text-black font-semibold"
                          : "bg-white/5 text-white/70 hover:bg-white/10"
                      }`}
                    >
                      <Icon size={12} />
                      {M.label}
                    </button>
                  );
                })}
              </div>

              {/* Results & Suggestions List */}
              <div className="flex-1 overflow-y-auto p-2 min-h-0" data-testid="global-search-body">
                {q.trim() === "" && type === "all" ? (
                  <div className="p-3 space-y-4">
                    {recent.length > 0 && (
                      <div>
                        <div className="flex items-center justify-between px-1 mb-2">
                          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/40 font-semibold">
                            <Clock size={11} /> Recent Searches
                          </div>
                          <button
                            data-testid="global-search-clear-recent"
                            onClick={clearRecent}
                            className="text-[10px] text-white/40 hover:text-white/70 uppercase tracking-widest transition-colors"
                          >
                            Clear
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {recent.map((r) => (
                            <button
                              key={r.q}
                              data-testid={`search-recent-${r.q}`}
                              onClick={() => setQ(r.q)}
                              className="px-3 py-1 rounded-full bg-white/5 hover:bg-white/10 border border-white/5 text-xs text-white/80 transition-colors"
                            >
                              {r.q}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {trending.length > 0 && (
                      <div>
                        <div className="flex items-center gap-1.5 px-1 mb-2 text-[10px] uppercase tracking-widest text-white/40 font-semibold">
                          <TrendingUp size={11} /> Trending Topics
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {trending.map((r) => (
                            <button
                              key={r.q}
                              data-testid={`search-trending-${r.q}`}
                              onClick={() => setQ(r.q)}
                              className="px-3 py-1 rounded-full bg-[#FFBF00]/5 hover:bg-[#FFBF00]/10 border border-[#FFBF00]/20 text-xs text-[#FFBF00]/90 transition-colors"
                            >
                              {r.q}
                              <span className="ml-1.5 text-[10px] text-[#FFBF00]/50">{r.count}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {recent.length === 0 && trending.length === 0 && (
                      <div className="text-center text-white/40 text-sm py-12">
                        Search creators, communities, servers, events, videos, products and builds.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1" data-testid="global-search-results">
                    {flatResults.length === 0 && !loading && (
                      <div className="text-center text-white/40 text-sm py-12">
                        No results {q ? `for "${q}"` : ""} in{" "}
                        <span className="text-white/60">{TYPE_META[type].label}</span>.
                      </div>
                    )}
                    {flatResults.map((r, i) => (
                      <ResultRow
                        key={`${r.type}-${r.id}-${i}`}
                        item={r}
                        active={i === active}
                        onSelect={onSelect}
                        testIdx={i}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-3.5 py-2.5 border-t border-white/10 text-[10px] text-white/40 uppercase tracking-widest shrink-0 bg-black/40">
                <div className="hidden sm:flex items-center gap-3">
                  <span><kbd className="text-white/60 bg-white/10 px-1 py-0.5 rounded">↑ ↓</kbd> Navigate</span>
                  <span><kbd className="text-white/60 bg-white/10 px-1 py-0.5 rounded">Enter</kbd> Select</span>
                  <span><kbd className="text-white/60 bg-white/10 px-1 py-0.5 rounded">Esc</kbd> Close</span>
                </div>
                <div className="sm:hidden text-white/40">
                  {flatResults.length > 0 ? `${flatResults.length} items` : ""}
                </div>
                {q.trim() && (
                  <button
                    data-testid="global-search-see-all"
                    onClick={onSubmit}
                    className="text-[#FFBF00] hover:underline font-semibold ml-auto"
                  >
                    See all results →
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}